import { useMemo } from 'react';
import { css } from '@emotion/react';
import { InvoiceModel } from '@frontend/api-invoices';
import { RefundReason, RefundRequestParams } from '@frontend/api-refunds';
import { Trans, useTranslation } from '@frontend/i18n';
import {
  DropdownField,
  EmailField,
  Modal,
  MoneyField,
  Text,
  useForm,
  useModalLoadingState,
  ValidatorFieldState,
  useAlert,
  Heading,
} from '@frontend/design-system';
import { formatCentsToCurrency, stringDollarsToCents } from '../../../utils/currency-helpers';
import { calcAvailableToRefund } from '../../../utils/page-helpers/refunds';

type RefundFormProps = {
  invoice: InvoiceModel;
  payment: InvoiceModel['payment'];
  refundAction: (params: RefundRequestParams) => Promise<void>;
};

type RefundReasonType = keyof typeof RefundReason;

const styles = {
  formContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-md);

    label {
      margin-bottom: var(--fabric-space-gap-xs);
      display: block;
    }
  `,
};

export const RefundForm = ({ refundAction, invoice }: RefundFormProps) => {
  const { t } = useTranslation('payments');
  const alert = useAlert();
  const { setLoading } = useModalLoadingState();

  const reasonMap = {
    FRAUDULENT: t('Fraudulent'),
    REQUESTED_BY_CUSTOMER: t('Requested by customer'),
    DUPLICATE: t('Duplicate'),
  };

  /**
   * { payment } is the last payment attempt, but it may not be successful.
   * We hit an edge case with terminals where a payment attempt succeeded, and was quickly followed
   * by another, failed attempt.
   *
   * This attempts to use a successful payment ID, if there is one,
   * to use that one, instead of just the last one across-the-board.
   */
  const payment = useMemo(() => {
    const { payment: lastPayment, payments } = invoice;

    if (lastPayment?.status === 'SUCCEEDED') {
      return lastPayment;
    }

    return payments?.find((p) => p.status === 'SUCCEEDED');
  }, [invoice]);

  const availableForRefund = calcAvailableToRefund(invoice);

  // FORMFIELDS
  const { formProps, getFieldProps, values } = useForm({
    fields: {
      amount: {
        type: 'money',
        required: true,
        //TODO: can update this when the design-system supports proper cents format
        value: payment?.paidAmount.toFixed() ? formatCentsToCurrency(availableForRefund) : '',
        validator: ({ value = '' }: ValidatorFieldState<'money'>) => {
          if (payment?.paidAmount && +value > availableForRefund / 100) {
            return t('Value exceeds full refund amount.');
          } else {
            return '';
          }
        },
      },
      reason: {
        type: 'dropdown',
        required: true,
      },
      receipt: {
        type: 'email',
        value: invoice.person.emailAddress === 'UNKNOWN' ? '' : invoice.person.emailAddress,
      },
    },
  });

  const balance = availableForRefund - (stringDollarsToCents(values.amount) ?? 0);

  const formattedRefundAmount = formatCentsToCurrency(stringDollarsToCents(values.amount) ?? 0);

  const handleSubmit = async () => {
    if (!payment?.paymentId) {
      alert.error(t('No payment id for this invoice'));
      return;
    }
    if (!values.reason) {
      alert.error(t('Please select a refund reason'));
      return;
    }
    setLoading(true);

    await refundAction({
      amount: stringDollarsToCents(values.amount),
      reason: values.reason as keyof typeof RefundReason,
      paymentId: payment?.paymentId,
      receiptEmail: values.receipt,
    });

    setLoading(false);
  };

  const amountProps = getFieldProps('amount');
  const reasonProps = getFieldProps('reason');
  const receiptProps = getFieldProps('receipt');

  // ids for labels
  const amountLabelId = `${amountProps.id}-label`;
  const reasonLabelId = `${reasonProps.id}-label`;
  const receiptLabelId = `${receiptProps.id}-label`;

  return (
    <>
      <Modal.Body>
        <form {...formProps} css={styles.formContainer}>
          <div>
            <Heading level={2}>{t('Refund Payment')}</Heading>
            <Text color='light'>
              <Trans t={t}>
                Full refund amount: <b>{formatCentsToCurrency(availableForRefund)}</b>
              </Trans>
            </Text>
          </div>

          <div>
            <Text id={amountLabelId} weight='bold' as='label'>
              {t('Refund Amount')}
            </Text>
            <MoneyField
              {...amountProps}
              aria-labelledby={amountLabelId}
              placeholder={t('Enter amount')}
              label=''
              max={availableForRefund / 100}
              data-trackingid='pay-portal-refund-txt-amount'
              helperText={t('Remaining balance: {{balance}}', {
                balance: formatCentsToCurrency(balance),
              })}
            />
          </div>

          <div>
            <Text id={reasonLabelId} weight='bold' as='label'>
              {t('Refund Reason')}
            </Text>
            <DropdownField
              {...reasonProps}
              aria-labelledby={reasonLabelId}
              placeholder={t('Select reason')}
              label=''
              data-trackingid='pay-portal-refund-drp-reason'
            >
              {Object.keys(RefundReason).map((key) => (
                <DropdownField.Option
                  key={key}
                  value={key}
                  data-trackingid={`pay-portal-refund-option-${key.toLocaleLowerCase()}`}
                >
                  {reasonMap[key as RefundReasonType]}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </div>

          <div>
            <Text id={receiptLabelId} weight='bold' as='label'>
              {t('Customer Receipt Email')}
            </Text>
            <EmailField
              {...receiptProps}
              aria-labelledby={receiptLabelId}
              placeholder='eg, dave.grohl@gmail.com'
              clearable
              label=''
              data-trackingid='pay-portal-refund-txt-email'
            />
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Refund {{amount}}', { amount: formattedRefundAmount }),
          trackingId: 'pay-portal-refund-btn-submit',
          onClick: handleSubmit,
          disabled: !values.amount || !stringDollarsToCents(values.amount) || balance < 0,
          hoverLabel:
            !values.amount || !stringDollarsToCents(values.amount)
              ? t('Refund amount must be greater than $0.00')
              : balance < 0
              ? t('Refund amount can’t exceed {{amount}}', { amount: formatCentsToCurrency(availableForRefund) })
              : undefined,
          showLabelWhenDisabled: true,
        }}
        secondary={{
          label: t('Cancel'),
          trackingId: 'pay-portal-refund-btn-cancel',
          onClick: () => true,
        }}
      />
    </>
  );
};
